import AddRoomButton from 'components/Common/AddRoomButton'
import { getTotalRoomsString } from 'components/Search/utils'
import noop from 'lib/function/noop'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import InlineGroup from 'components/Common/InlineGroup'
import OccupancyItem from './OccupancyItem'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { AdultLabel, formatOccupantsShort, generateRoomOccupants } from 'lib/offer/occupancyUtils'
import TextButton from '../../Luxkit/Button/TextButton'
import BodyText from '../../Luxkit/Typography/BodyText'
import { fireInteractionEvent } from 'api/googleTagManager'
import { multiRoomTypesOccupancy } from 'analytics/eventDefinitions'
import cn from 'clsx'
import config from 'constants/config'

const Container = styled.div`
  ${mediaQueryUp.tablet} {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    max-height: min(calc(var(--vh, 1vh) * 100 - 220px), 820px);
  }
`

const OccupancyPickerRoot = styled.div`
  ${mediaQueryUp.tablet} {
    padding: ${rem(32)} ${rem(24)} ${rem(20)};
    overflow: auto;

    &.smallView {
      padding: 0
    }
  }

  > * + * {
    margin-top: ${rem(20)}
  }
`

const FooterInfo = styled(InlineGroup)`
  display: none;

  ${mediaQueryUp.tablet} {
    align-items: center;
    background-color: ${props => props.theme.palette.neutral.eight};
    border-bottom-left-radius: ${props => props.theme.borderRadius.medium};
    border-bottom-right-radius: ${props => props.theme.borderRadius.medium};
    box-shadow: ${props => props.theme.shadow.flat.small};
    display: flex;
    justify-content: flex-end;
    padding: ${rem(16)} ${rem(24)};
  }

  > * + * {
    margin-left: ${rem(16)};
  }
`

interface Props {
  occupancies: Array<App.Occupants | App.RoomOccupants>;
  maxRooms?: number;
  noServiceContact?: boolean;
  capacities?: Array<App.PackageRoomCapacity>;
  maxChildAge?: number;
  maxInfantAge?: number;
  saleUnit?: string;
  onChange?: (occupancies: Array<App.Occupants | App.RoomOccupants>) => void;
  onApply?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  applyText?: string;
  name?: string;
  submit?: boolean;
  'data-testid'?: string;
  className?: string; // css for container div
  disclaimer?: string;
  childrenAgeLabel?: string
  enableMultiRoomTypesEvents?: boolean;
  smallView?: boolean;
  contactBoxStyle?: boolean;
  enableMultiRoom?: boolean
  adultLabelType?: AdultLabel;
  showChildren?: boolean;
  bundleOfferId?: string;
  offerType?: App.OfferType;
  offerId?: string;
  disableSaleUnit?: boolean
}

const OccupancyPicker = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    occupancies,
    capacities,
    maxChildAge,
    maxInfantAge,
    saleUnit = 'room',
    onChange = noop,
    onApply,
    maxRooms,
    noServiceContact,
    applyText = 'Apply',
    name = 'occupants', // not user-visible - don't translate
    submit,
    className,
    disclaimer,
    childrenAgeLabel = 'at time of check-in',
    enableMultiRoomTypesEvents,
    smallView = false,
    contactBoxStyle,
    enableMultiRoom,
    adultLabelType,
    showChildren,
    bundleOfferId,
    offerType,
    offerId,
    disableSaleUnit,
  } = props

  const isInstanceOfRoomOccupants = useMemo(() => occupancies.length && ('roomId' in occupancies[0]), [occupancies])

  const onAddAnotherRoom = useCallback(() => {
    const newRoom = isInstanceOfRoomOccupants ? generateRoomOccupants(config.search.defaultOccupants) : { ...config.search.defaultOccupants }
    const newOccupancies = [...occupancies, newRoom]
    onChange(newOccupancies)

    if (enableMultiRoomTypesEvents) {
      fireInteractionEvent(multiRoomTypesOccupancy('add-room', newOccupancies))
    }
  }, [occupancies, onChange, isInstanceOfRoomOccupants, enableMultiRoomTypesEvents])

  const onRemove = useCallback((index: number) => {
    const newOccupancies = [...occupancies]
    newOccupancies.splice(index, 1)
    onChange(newOccupancies)

    if (enableMultiRoomTypesEvents) {
      fireInteractionEvent(multiRoomTypesOccupancy('remove-room', newOccupancies))
    }
  }, [occupancies, onChange, enableMultiRoomTypesEvents])

  const onReset = useCallback((index: number) => {
    const newOccupancies = [...occupancies]
    newOccupancies[index] = { ...newOccupancies[index], ...config.search.defaultOccupants }
    onChange(newOccupancies)

    if (enableMultiRoomTypesEvents) {
      fireInteractionEvent(multiRoomTypesOccupancy('reset-room', newOccupancies))
    }
  }, [occupancies, onChange, enableMultiRoomTypesEvents])

  const onItemChange = useCallback((index: number, newOccupancy: App.Occupants, eventLabel?: string) => {
    const newOccupancies = [...occupancies]
    newOccupancies[index] = { ...newOccupancies[index], ...newOccupancy }
    onChange(newOccupancies)

    // Only for incrementing
    if (enableMultiRoomTypesEvents && eventLabel) {
      fireInteractionEvent(multiRoomTypesOccupancy(eventLabel, newOccupancies))
    }
  }, [occupancies, onChange, enableMultiRoomTypesEvents])
  const totalOccupantsString = useMemo(() => formatOccupantsShort(occupancies, undefined, adultLabelType), [occupancies, adultLabelType])
  const totalRoomsString = useMemo(() => disableSaleUnit ? '' : getTotalRoomsString(occupancies, saleUnit), [occupancies, saleUnit, disableSaleUnit])
  return (
    <Container className={className}>
      <OccupancyPickerRoot className={cn({ smallView })} data-testid={props['data-testid']} ref={ref}>
        {!!disclaimer && <BodyText variant="medium" colour="neutral-two" >{disclaimer}</BodyText>}
        {occupancies.map((occupants, index) => <OccupancyItem
          smallView={smallView}
          key={index}
          itemId={index}
          occupancy={occupants}
          title={disableSaleUnit ? undefined : bundleOfferId ? 'Guests' : `${saleUnit} ${index + 1}`}
          showRemove={index > 0}
          onReset={onReset}
          onRemove={onRemove}
          maxChildAge={maxChildAge}
          maxInfantAge={maxInfantAge}
          onChange={onItemChange}
          capacities={capacities}
          childrenAgeLabel={childrenAgeLabel}
          name={name}
          adultLabelType={adultLabelType}
          showChildren={showChildren}
          offerType={offerType}
          offerId={offerId}
        />)}
        {enableMultiRoom && <div>
          <AddRoomButton
            maxRooms={maxRooms}
            saleUnit={saleUnit}
            roomsCount={occupancies.length}
            onClick={onAddAnotherRoom}
            noServiceContact={noServiceContact}
            contactBoxStyle={contactBoxStyle}
          />
        </div>}
      </OccupancyPickerRoot>
      {!smallView && <FooterInfo vertical="center">
        <BodyText variant="large">
          <strong>{totalOccupantsString}</strong> {!bundleOfferId ? totalRoomsString : ''}
        </BodyText>
        <TextButton
          kind="primary"
          type={submit ? 'submit' : 'button'}
          onClick={onApply}
          data-testid={`${props['data-testid']}-apply`}
        >
          {applyText}
        </TextButton>
      </FooterInfo>}
    </Container>
  )
})

OccupancyPicker.displayName = 'OccupancyPicker'

export default React.memo(OccupancyPicker)
